import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Host,
  HostListener,
  Input,
  Optional, Output, QueryList,
  SkipSelf, ViewChildren
} from '@angular/core';
import {ControlContainer, NG_VALUE_ACCESSOR} from '@angular/forms';
import {Buffer} from 'buffer';
import {isEmpty, isNil} from 'lodash';
import {DeviceCamera} from 'src/app/infrastructure/device/device-camera';
import {Alert} from 'src/app/infrastructure/service/alert.service';
import {Translator} from 'src/app/infrastructure/translation/translator.service';
import {BaseInputComponent} from 'src/app/ui/shared/component/input/base-input.component';

type InputColor = 'primary' | 'secondary' | 'light' | 'dark' | 'grey';

@Component({
  selector: 'app-uploader',
  template: `
    <app-form-input-decorator *ngIf="componentIsReady" [formInput]="this">
      <div class="border-2 text-center text-primary ion-activatable ripple-parent p-2 relative cursor-pointer">
        <div class="p-5">
          <ion-img class="w-full" [src]="blobURL" *ngIf="showImage && blobURL"></ion-img>
          <div class="absolute top-0 rounded my-auto bg-surface-container-lowest right-0 flex align-center" *ngIf="blobURL"
            (click)="removeFile()"
          >
            <ion-icon name="close-outline" class="text-3xl"></ion-icon>
          </div>
          <div class="absolute top-0 rounded my-auto bg-surface-container-lowest right-7 flex align-center" *ngIf="blobURL"
            (click)="(isCameraAvailable) ? selectSource() : file.click()"
          >
            <ion-icon name="create" class="text-3xl"></ion-icon>
          </div>
          <div
            (click)="(isCameraAvailable) ? selectSource() : file.click()"
          >
          <div class="text-center mt-4 empty:hidden">{{ fileName }}</div>
          <ng-container *ngIf="!hasSelectedFile()">
            <div>
              <ion-icon *ngIf="isCameraAvailable" lazy="true" name="camera-outline" class="text-2xl"></ion-icon>
              <ion-icon *ngIf="!isCameraAvailable" lazy="true" name="cloud-upload" class="text-2xl"></ion-icon>
            </div>
            <a class="text-{{color}}">{{ inputlabel() }}</a>
          </ng-container>
          <input type="file"
                 class="ion-hide"
                 id="file-input"
                 accept="image/png, image/jpeg, application/pdf"
                 #file
                 (click)="$event.stopPropagation()"
                 [attr.data-test]="testData"
          />
          </div>
          <ion-ripple-effect></ion-ripple-effect>
        </div>
      </div>
    </app-form-input-decorator>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UploadComponent),
      multi: true
    }
  ]
})
export class UploadComponent extends BaseInputComponent<File|null> {
  @ViewChildren('file') fileInput: QueryList<ElementRef>;
  @Input() type: string;
  @Input() nativeAppLabel = '';
  @Input() label = '';
  @Input() progress: number;
  @Input() readonly = false;
  @Input() showImage = true;
  @Input() color: InputColor = 'primary';
  @Input() testData: string;
  @Output() fileRemoved = new EventEmitter();

  public componentIsReady = false;
  public isCameraAvailable: boolean;
  blobURL = '';
  fileName = '';

  constructor(
    private readonly camera: DeviceCamera,
    private readonly translator: Translator,
    host: ElementRef<HTMLInputElement>,
    private readonly alert: Alert,
    @Optional() @Host() @SkipSelf()  controlContainer: ControlContainer,
  ) {
    super(controlContainer, host);
  }

  @HostListener('change', ['$event.target.files']) onHostChange( event: FileList ) {
    const file = event && event.item(0);
    this.emitFile(file);
  }

  writeValue(value: File | null): void {
    this.host.nativeElement.value = value? value.name : '';
    this.value = value? value : null;
    this.blobURL = value? URL.createObjectURL(value) : null;
    this.fileName = value? value.name : '';
  }

  public async selectSource(): Promise<void> {
    await this.alert.present({
      header: this.translator.trans('form.fileUploader.options.modalTitle'),
      message: this.translator.trans('form.fileUploader.options.modalDescription'),
      buttons: [
        {
          text: this.translator.trans('form.fileUploader.options.uploadFile'),
          handler: () => {
            this.fileInput.get(0).nativeElement.click();
          }
        },
        {
          text: this.translator.trans('form.fileUploader.options.uploadPicture'),
          handler: () => {
            this.takePicture();
          }
        },
      ]
    });
  }

  public async removeFile(): Promise<void> {
    this.writeValue(null);
    this.onChange();
    this.fileRemoved.emit();
  }

  public hasSelectedFile(): boolean {
    return !isEmpty(this.fileName);
  }

  public inputlabel(): string {
    if (this.isCameraAvailable) {
      return !isEmpty(this.nativeAppLabel)
        ? this.nativeAppLabel
        : this.translator.trans('form.fileUploader.uploadPicture');
    }

    return !isEmpty(this.label) ? this.label : this.translator.trans('form.fileUploader.uploadFile');
  }

  protected async onInit() {
    super.onInit();
    this.isCameraAvailable = this.camera.isAvailable() && (await this.camera.hasPermissions());
    this.componentIsReady = true;
  }

  private emitFile(file: File): void {
    this.onChange(file);
    this.value = file;
    this.loadImageFromDevice(file);
  }

  private loadImageFromDevice(file: File) {
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = () => {
      this.fileName = file.name;
      if (file.type === 'application/pdf') {
        this.blobURL = '';
        return;
      }
      // get the blob of the image:
      const blob: Blob = new Blob([new Uint8Array((reader.result as ArrayBuffer))]);
      // create blobURL, such that we could use it in an image element:
      this.blobURL = URL.createObjectURL(blob);
    };

    reader.onerror = (error) => {
      throw new Error('Error uploading file');
    };
  }

  private async takePicture(): Promise<void> {
    const imageData = await this.camera.takePicture();

    if (isNil(imageData)) {
      return;
    }

    const file = new File(
      [Buffer.from(imageData.base64String, 'base64')],
      'file.'+imageData.format,
      {type: imageData.mimeType}
    );
    this.emitFile(file);
  };
}
